import {
  copyListToUser,
  deleteList,
  downloadList,
  editList,
  getFields,
  getFirstPageLists,
  getListsQuick,
  requestPage,
} from "../../../../../api/ContactAPI";
import { nameColumn } from "../../../../../layouts/Table/columns/nameColumn";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Flex,
  Group,
  Loader,
  NumberFormatter,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import MainTable from "../../../../../layouts/Table/MainTable";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import numberColumn from "../../../../../layouts/Table/columns/numberColumn";
import { sharingColumn } from "../../../../../layouts/Table/columns/sharingColumn";
import { tagsColumn } from "../../../../../layouts/Table/columns/tagsColumn";
import ListActions from "../../actions/ListActions";
import { IconUsers } from "@tabler/icons-react";
import SelectionMover from "../../../../../components/TableComponents/SelectionMover";
import SelectionSharer from "../../../../../components/TableComponents/SelectionSharer";
import SelectionDeleter from "../../../../../components/TableComponents/SelectionDeleter";
import MergeLists from "../../components/merge/MergeLists";
import { useNavigate, useParams } from "react-router-dom";
import { useListStore } from "../../../../../stores/ListStore";
import { getListOrigin, getRowClasses } from "../listing";
import dateColumn from "../../../../../layouts/Table/columns/dateColumn";
import Jobs from "../../components/jobs/Jobs";
import Pinner from "../../../../../components/TableComponents/Pinner";
import {
  formatThousand,
  transformFilterToServer,
} from "../../../../../functions/utilFunctions";
import ActionsCellChevron from "../../components/ActionsCellChevron";
import ListEnrichMultiple from "../../actions/ListEnrichMultiple";
import SelectionCopyToUser from "../../../../../components/View/SelectionCopyToUser";
import SelectionListDownload from "../../actions/SelectionListDownload";

const ContactListsListing = () => {
  const lang = useLanguage((s) => s.language);
  const apiRef = useGridApiRef();
  const { contactListId } = useParams();
  const listId = useMemo(() => parseInt(contactListId), [contactListId]);

  const columns = useMemo(() => {
    return [
      numberColumn(),
      nameColumn({
        headerName: lang.prospects.list_name,
        disableLink: true,
        editFunction: () => editList,
        handleClick: () => {},
        minWidth: 200,
        flex: 1,
        leftSection: function (row) {
          return (
            <Avatar
              color="initials"
              name={row.name}
              radius={"md"}
              size={36}
              variant="filled"
            />
          );
        },
        apiRef,
      }),
      {
        field: "jobs",
        headerName: lang.contact_lists.jobs.column_name,
        width: 160,
        filterable: false,
        renderCell: ({ row }) => {
          return (
            <Jobs row={row} apiRef={apiRef} isList setLoading={setLoading} />
          );
        },
      },
      {
        field: "number_of_contacts",
        headerName: lang.contact_lists.columns.number_of_contacts,
        type: "number",
        align: "left",
        headerAlign: "left",
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row, value }) => {
          return (
            <Tooltip
              label={`${formatThousand(
                row?.number_of_emails || 0,
              )} emails, ${formatThousand(
                row?.number_of_linkedin_url || 0,
              )} LinkedIn`}
            >
              <Group id={"loading-column" + row.id}>
                <NumberFormatter value={value} thousandSeparator=" " />

                {(row.state_details?.find((x) => x.state === "InProgress") ||
                  row.state_details?.find((x) => x.state === "InQueue")) && (
                  <Tooltip
                    label={
                      lang.contact_lists.jobs.in_progress
                        .loading_indicator_tooltip
                    }
                  >
                    <Loader color="blue" type="dots" size={"xs"} />
                  </Tooltip>
                )}
              </Group>
            </Tooltip>
          );
        },
      },
      dateColumn({ filterable: false }),
      tagsColumn({
        apiRef,
        editFunction: () => editList,
        filterable: false,
      }),
      sharingColumn({ apiRef, editFunction: () => editList }),
    ];
  }, []);

  const listingLoad = useListStore((s) => s.listingLoad);
  const setListingLoad = useListStore((s) => s.setListingLoad);
  const [rows, setRows] = useState([]);
  const [pageLock, setPageLock] = useState(false);
  const [serverInfo, setServerInfo] = useState({
    number_of_pages: 0,
    number_of_results: null,
    url: "",
  });
  const [loading, setLoading] = useState(false);

  const handleLoading = useCallback(
    ({ toFirstPage }) => {
      if (loading) return;
      setLoading(true);

      if (toFirstPage) {
        setPageLock(true);
        apiRef.current.setPage(0);
      }

      const pagination = apiRef.current.state.pagination.paginationModel;
      const per_page = apiRef.current.state.pagination.paginationModel.pageSize;
      const query =
        apiRef.current.state.filter.filterModel.quickFilterValues?.join(" ");
      const tableFilter = apiRef.current.state.filter.filterModel;

      let filter = {
        mode: "and",
        values: [
          {
            field_name: "folder_id",
            type: "does_not_exist",
            value: "",
          },
        ],
      };

      if (query) {
        filter.values.push({
          mode: "or",
          values: [
            {
              field_name: "name",
              type: "contains",
              value: query,
            },
            {
              field_name: "id",
              type: "contains",
              value: query,
            },
          ],
        });
      }

      if (tableFilter?.items?.length > 0) {
        filter.values.push(transformFilterToServer(tableFilter));
      }

      let main =
        pagination.page > 0
          ? () =>
              requestPage(
                serverInfo.url,
                pagination.page + 1,
                false,
                `?options=${JSON.stringify({
                  page: toFirstPage ? 1 : pagination.page + 1,
                  per_page,
                  query,
                  filter,
                })}`,
              )
          : () =>
              getFirstPageLists({
                page: toFirstPage ? 1 : pagination.page + 1,
                per_page,
                query,
                filter,
              });
      main()
        .then((response) => {
          let results = [...response.data.results];

          let newServerInfo = {
            ...response.data,
            url: response.data.current_page.slice(0, -1), // For pagination
          };
          if (
            listId &&
            !results.find((x) => x.id === listId) &&
            rows.length === 0
          ) {
            const currentList = useListStore.getState().currentList;

            if (currentList) {
              results.unshift(currentList);
            }
          }

          setServerInfo(newServerInfo);
          setRows(results);
        })
        .catch(() => {
          setLoading(false);
        })
        .finally(() => {
          setPageLock(false);
          setLoading(false);
          setListingLoad(false);
        });
    },
    [apiRef, loading, serverInfo.url],
  );

  const toolbarActions = useCallback(
    ({ selectedIds, handleClose }) => {
      return (
        <>
          {/* <Button
            variant="light"
            color="primary"
            disabled={selectedIds.length === 0}
            leftSection={<IconDownload size={18} />}
            justify="start"
            className="to-blue"
          >
            {lang.contact_lists.download}
          </Button> */}

          <ListEnrichMultiple
            onFinish={() => {
              handleLoading({ isRefresh: true, toFirstPage: false });
            }}
            selectedListIds={selectedIds}
            closeMenu={handleClose}
          />
          <SelectionSharer
            handleShare={editList}
            apiRef={apiRef}
            setTableLoading={setLoading}
            ids={selectedIds}
          />
          <MergeLists onFinish={handleClose} selectedIds={selectedIds} />
          <SelectionListDownload
            rows={rows}
            closeMenu={handleClose}
            handler={downloadList}
            selectedIds={selectedIds}
          />
          <SelectionCopyToUser
            closeMenu={handleClose}
            handler={copyListToUser}
            selectedIds={selectedIds}
          />
          <SelectionMover
            handleEdit={editList}
            apiRef={apiRef}
            setTableLoading={() => {
              handleLoading({ isRefresh: true, toFirstPage: false });
            }}
            ids={selectedIds}
            folderType={"ContactList"}
          />
          <SelectionDeleter
            handleDelete={deleteList}
            apiRef={apiRef}
            setTableLoading={setLoading}
            ids={selectedIds}
            setRows={setRows}
          />
        </>
      );
    },
    [apiRef, lang.contact_lists.download, rows],
  );

  const navigate = useNavigate();
  const handleRowClick = useCallback(
    ({ row }) => {
      navigate(`/contact-lists/${row.id}`);
    },
    [navigate],
  );

  const actionsCell = useCallback(({ row }) => {
    return (
      <Group wrap="nowrap" gap={4} pos={"relative"}>
        {/* <QuickCampaign current={row} /> */}
        <ThemeIcon variant="subtle">{getListOrigin(row)}</ThemeIcon>

        <ListActions
          current={row}
          onFinish={() =>
            handleLoading({ isRefresh: false, toFirstPage: true })
          }
        />

        <ActionsCellChevron row={row} handleRowClick={handleRowClick} />

        <Pinner row={row} editor={editList} apiRef={apiRef} setRows={setRows} />
      </Group>
    );
  }, []);

  useEffect(() => {
    if (contactListId === "none" && rows.length > 0) {
      navigate(`/contact-lists/${rows[0].id}`, { replace: true });
    }
  }, [contactListId, rows]);

  const handlePagination = useCallback(
    (v, r) => {
      if (!pageLock) {
        handleLoading({ isRefresh: false, toFirstPage: false });
      }
    },
    [pageLock, handleLoading],
  );
  const handleFilterChange = useCallback(
    (filterModel, reason) => {
      if (!pageLock) {
        handleLoading({ isRefresh: false, toFirstPage: true });
      }
    },
    [pageLock, handleLoading],
  );

  useEffect(() => {
    if (listingLoad) {
      handleLoading({ isRefresh: true });
      getListsQuick();
    }
  }, [listingLoad]);

  useEffect(() => {
    getFields();
  }, []);

  return (
    <Flex w={"100%"} pos={"relative"} flex={1} miw={400}>
      <Group
        pos={"absolute"}
        top={64}
        pt={8}
        w={"100%"}
        style={{
          borderTop: "1px solid #00000012",
        }}
      >
        <Button
          style={{ zIndex: 7 }}
          variant={contactListId === "all" ? "filled" : "subtle"}
          fz={20}
          fw={600}
          radius={"md"}
          size="md"
          w={"100%"}
          justify="start"
          pl={7}
          h={52}
          leftSection={
            <ThemeIcon
              size={40}
              radius={"md"}
              color={contactListId === "all" ? "white" : "primary"}
            >
              <IconUsers />
            </ThemeIcon>
          }
          onClick={() => {
            if (contactListId !== "all") navigate("/contact-lists/all");
            else navigate("/contact-lists/none");
          }}
        >
          {lang.prospects.all_contacts}
        </Button>
      </Group>

      <MainTable
        isClient={false}
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        rowCount={serverInfo?.number_of_results}
        loading={loading}
        setLoading={(v) => {
          handleLoading({
            isRefresh: true,
            toFirstPage: false,
          });
        }}
        id={"list-table"}
        actionsCell={actionsCell}
        onPaginationChange={handlePagination}
        hasFolder
        pageName={"contact_lists"}
        pageOrderName={"contact_list_order"}
        actionsCellWidth={112}
        defaultPerPage={15}
        // disablePerPage
        onRowClick={handleRowClick}
        toolbarActions={toolbarActions}
        onFilterChange={handleFilterChange}
        defaultSort={[{ field: "id", sort: "desc" }]}
        getRowClassName={(params) =>
          getRowClasses({
            params,
            mainId: contactListId,
            integerId: listId,
            isHoverSource: true,
          })
        }
        tableSx={{
          "& .table-toolbar": {
            minWidth: 450,
          },
          "& .MuiDataGrid-pinnedColumnHeaders": {
            border: "none !important",
            background: "none !important",
          },
          "& .MuiDataGrid-pinnedColumns": {
            border: "none !important",
            background: "none !important",
          },
          "& .MuiDataGrid-cell": {
            border: "none !important",
          },
          "& .MuiDataGrid-columnHeaders": {
            border: "none !important",
          },
          "& .MuiDataGrid-main": {
            border: "none !important",
            marginTop: 8,
            background: "none !important",
          },
          "& .table-row": {
            cursor: "pointer",
            borderRadius: 4,
            marginBottom: 1,
            background: "#fff !important",
            border: "1px solid var(--mantine-color-primary-1)",
          },
          "& .MuiDataGrid-pinnedColumns--right": {
            background: "var(--mantine-color-primary-2) !important",
            "& .table-row": {
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderLeft: "none",
            },
          },
          "& .MuiDataGrid-pinnedColumns--left": {
            background: "var(--mantine-color-primary-2) !important",
            "& .table-row": {
              // borderTopLeftRadius: "8px !important",
              // borderBottomLeftRadius: 4,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              borderRight: "none",
            },
          },
          "& .MuiDataGrid-virtualScrollerRenderZone .table-row": {
            borderRadius: "0 !important",
          },
          // "& .MuiDataGrid-selectedRowCount": {
          //   visibility: "hidden",
          // },
          "& .MuiPagination-root": {
            left: 0,
            transform: "none",
          },
          "& .MuiDataGrid-footerContainer": {
            border: 0,
          },
          "& .table-search": {
            width: "180px !important",
          },
        }}
      />
    </Flex>
  );
};

export default ContactListsListing;
